import { Link } from "react-router";
import logoFull from "@/assets/brand/guestbook-logo.png";
import logoMark from "@/assets/brand/guestbook-mark.png";
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "@/shared/lib/variants";

/** The brand's accessible name, used wherever the logo represents Guestbook. */
const LABEL = "Guestbook by Artisaire";

const ASSET = { full: logoFull, mark: logoMark } as const;

const logo = cva("inline-block w-auto select-none", {
  variants: {
    variant: {
      /** Horizontal lockup (mark + GUESTBOOK wordmark). Default. */
      full: "h-8",
      /** Arch mark only — for tight spaces like the mobile header. */
      mark: "h-9",
    },
  },
  defaultVariants: {
    variant: "full",
  },
});

interface LogoProps extends VariantProps<typeof logo> {
  /** Where the logo links. Defaults to /app. Ignored when `presentation="static"`. */
  to?: string;
  onClick?: () => void;
  className?: string;
  /**
   * `"link"` (default) renders a navigable logo for the app shell.
   * `"static"` renders a non-interactive logo for splash / loading / error
   * surfaces where navigation isn't an affordance (e.g. the auth loading
   * overlay).
   */
  presentation?: "link" | "static";
}

/**
 * Brand logo — the single sanctioned way to render the Guestbook lockup. The
 * brand artwork (transparent PNG, warm ink for light surfaces) lives only here
 * so the logo is never duplicated. `variant="full"` is the horizontal lockup;
 * `variant="mark"` is the arch mark alone for tight spaces. Defaults to a Link
 * so the logo is a navigable affordance back to /app; `presentation="static"`
 * renders a plain image for splash / loading / error surfaces.
 */
export function Logo({
  to = "/app",
  onClick,
  className,
  presentation = "link",
  variant = "full",
}: LogoProps) {
  const src = ASSET[variant ?? "full"];
  const imgClasses = cn(logo({ variant }), className);

  if (presentation === "static") {
    return <img src={src} alt={LABEL} className={imgClasses} />;
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      aria-label={`${LABEL} — home`}
      className="inline-flex rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
    >
      <img src={src} alt="" aria-hidden className={imgClasses} />
    </Link>
  );
}
